import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme, Paper, Divider, List, ListItem, ListItemIcon, ListItemText, ListSubheader, Grid2, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { parseDateToDDMMYYYY2 } from "../../../../../helpers/dateTransform";
import { CalendarCircleBold, BoldJudge } from "../../../../Icon";
import Icon from "../../../../Icon/Icon";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { useReportContext } from "../../../ReportContext/useReportContext";
import { ModalAttachments } from "../../../../Modal/Attachments/ModalAttachments";
import { catalogByPart, getMeasures, getSanctions, getCompensations } from "../../../../../apis";
import { useParams } from "react-router-dom";
import Comments from "../../Information/Comments";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";

export const ResolutionSummary = ({ reportDetail }) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const { id } = useParams();
  const { evaluated_facts, measureOptions, sanctionsOptions, benefitOptions, involvedType } = useReportContext();
  const { iconName, color, label } =
    evaluated_facts[reportDetail?.resolution?.facts_verified] || {};

  const [openModal, setOpenModal] = useState(false);
  const [investigationResultOpts, setInvestigationResultOpts] = useState([]);
  const [detectedVulnerabilitiesOpts, setDetectedVulnerabilitiesOpts] = useState([]);
  const [investigatingAreaOpts, setInvestigatingAreaOpts] = useState([]);
  const [regulatoryBodyOpts, setRegulatoryBodyOpts] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [sanctions, setSanctions] = useState([]);
  const [compensations, setCompensations] = useState([]);

  // Cargar catálogos y sanciones
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const investigationResultResp = await catalogByPart({
          is_own: true,
          catalogue: "investigationResult",
          path: "/",
        });
        if (investigationResultResp?.data && Array.isArray(investigationResultResp.data)) {
          const formattedOptions = investigationResultResp.data.map((item) => ({
            value: item.key,
            label: item.label,
          }));
          setInvestigationResultOpts(formattedOptions);
        }

        const vulnerabilitiesResp = await catalogByPart({
          is_own: true,
          catalogue: "detectedVulnerabilities",
          path: "/",
        });
        if (vulnerabilitiesResp?.data && Array.isArray(vulnerabilitiesResp.data)) {
          const formattedOptions = vulnerabilitiesResp.data.map((item) => ({
            value: item.key,
            label: item.label,
          }));
          setDetectedVulnerabilitiesOpts(formattedOptions);
        }

        const investigatingAreaResp = await catalogByPart({
          is_own: true,
          catalogue: "investigatingArea",
          path: "/",
        });
        if (investigatingAreaResp?.data && Array.isArray(investigatingAreaResp.data)) {
          const formattedOptions = investigatingAreaResp.data.map((item) => ({
            value: item.key,
            label: item.label,
          }));
          setInvestigatingAreaOpts(formattedOptions);
        }

        const regulatoryBodyResp = await catalogByPart({
          is_own: true,
          catalogue: "regulatoryBody",
          path: "/",
        });
        if (regulatoryBodyResp?.data && Array.isArray(regulatoryBodyResp.data)) {
          const formattedOptions = regulatoryBodyResp.data.map((item) => ({
            value: item.key,
            label: item.label,
          }));
          setRegulatoryBodyOpts(formattedOptions);
        }
      } catch (error) {
        console.error("Error loading catalogs:", error);
      }
    };

    const loadSanctionsData = async () => {
      try {
        const measuresResp = await getMeasures(id, "all");
        setMeasures(measuresResp || []);

        const sanctionsResp = await getSanctions(id, "all");
        setSanctions(sanctionsResp || []);

        const compensationsResp = await getCompensations(id, "all");
        setCompensations(compensationsResp || []);
      } catch (error) {
        console.error("Error loading sanctions data:", error);
      }
    };

    loadCatalogs();
    loadSanctionsData();
  }, [id]);

  const hdlOpenModal = () => setOpenModal(true);
  const hdlCloseModal = () => setOpenModal(false);

  // Función para obtener label desde opciones
  const getLabelByValue = (options, value) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : null;
  };

  // Si no hay resolución guardada, no mostrar nada
  if (!reportDetail?.resolution?.facts_verified) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mb: 3,
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant="h6" color="primary" gutterBottom>
        {t("Resolution.general_details_resolution_report")}
      </Typography>

      {reportDetail?.resolution?.files && reportDetail.resolution.files.length > 0 && (
        <ModalAttachments
          open={openModal}
          onClose={hdlCloseModal}
          files={reportDetail.resolution.files}
        />
      )}

      <Box sx={{ position: "relative" }} className="border-bottom">
        <Box display="flex" alignItems="center" gap={1} my={1}>
          <CalendarCircleBold color={theme.palette.primary.main} />
          <Typography color="secondary" variant="subtitle1">
            {t("Resolution.resolution_date")}:
          </Typography>
          <Typography color="primary" variant="h6">
            {parseDateToDDMMYYYY2(reportDetail.resolution.date)}
          </Typography>
        </Box>

        {iconName && (
          <Box display="flex" alignItems="center" gap={1} my={1}>
            <Icon name={iconName} color={theme.palette[color]?.main} />
            <Typography color="secondary" variant="subtitle1">
              {label}
            </Typography>
          </Box>
        )}

        {reportDetail?.resolution?.investigation_result && (
          <Box my={1}>
            <Typography variant="subtitle2" color="primary">
              {t("Resolution.investigation_result")}:
            </Typography>
            <Typography>
              {reportDetail.resolution.investigation_result_label ||
                investigationResultOpts.find(
                  (opt) => opt.value === reportDetail.resolution.investigation_result
                )?.label ||
                reportDetail.resolution.investigation_result}
            </Typography>
          </Box>
        )}

        {reportDetail?.resolution?.detected_vulnerabilities && (
          <Box my={1}>
            <Typography variant="subtitle2" color="primary">
              {t("Resolution.detected_vulnerabilities")}:
            </Typography>
            <Typography>
              {reportDetail.resolution.detected_vulnerabilities_label ||
                detectedVulnerabilitiesOpts.find(
                  (opt) => opt.value === reportDetail.resolution.detected_vulnerabilities
                )?.label ||
                reportDetail.resolution.detected_vulnerabilities}
            </Typography>
          </Box>
        )}

        {reportDetail?.resolution?.damage_value && (
          <Box my={1}>
            <Typography variant="subtitle2" color="primary">
              {t("Resolution.damage_value_usd")}:
            </Typography>
            <Typography>{reportDetail?.resolution?.damage_value}</Typography>
          </Box>
        )}

        {reportDetail?.resolution?.recovered_value && (
          <Box my={1}>
            <Typography variant="subtitle2" color="primary">
              {t("Resolution.recovered_value_usd")}:
            </Typography>
            <Typography>{reportDetail?.resolution?.recovered_value}</Typography>
          </Box>
        )}

        {reportDetail?.resolution?.redirected_area && (
          <Box my={1}>
            <Typography variant="subtitle2" color="primary">
              {t("Resolution.redirected_area")}:
            </Typography>
            <Typography>
              {reportDetail.resolution.redirected_area_label ||
                investigatingAreaOpts.find(
                  (opt) => opt.value === reportDetail.resolution.redirected_area
                )?.label ||
                reportDetail.resolution.redirected_area}
            </Typography>
          </Box>
        )}

        {reportDetail?.resolution?.regulatory_body && (
          <Box my={1}>
            <Typography variant="subtitle2" color="primary">
              {t("Resolution.regulatory_body")}:
            </Typography>
            <Typography>
              {reportDetail.resolution.regulatory_body_label ||
                regulatoryBodyOpts.find(
                  (opt) => opt.value === reportDetail.resolution.regulatory_body
                )?.label ||
                reportDetail.resolution.regulatory_body}
            </Typography>
          </Box>
        )}

        {reportDetail?.resolution?.comment && (
          <Box my={1}>
            <Typography variant="subtitle2" color="primary">
              {t("Resolution.general_comments_on_resolution")}
            </Typography>
            <Typography>{reportDetail?.resolution?.comment}</Typography>
          </Box>
        )}
      </Box>

      <Box mt={2}>
        <EGButton
          iconName="attach_circle"
          label={
            reportDetail?.resolution?.files?.length === 0
              ? t("Resolution.no_evidence")
              : t("Resolution.evidence")
          }
          disabled={!reportDetail?.resolution?.files || reportDetail.resolution.files.length === 0}
          onClick={hdlOpenModal}
        />
      </Box>

      {/* Secciones de Sanciones */}
      {(measures.length > 0 || sanctions.length > 0 || compensations.length > 0) && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" color="primary" gutterBottom>
            {t("Resolution.sanctions_actions")}
          </Typography>
        </>
      )}

      {/* Medidas Correctivas/Preventivas */}
      {measures.length > 0 && (
        <Box my={2}>
          <Typography
            variant="subtitle1"
            color="secondary"
            fontWeight={600}
            gutterBottom
            sx={{
              backgroundColor: theme.palette.aquaSky.main,
              color: theme.palette.background.paper,
              px: 2,
              py: 1,
              borderRadius: 1
            }}
          >
            {t("Resolution.corrective_preventive_measures")}
          </Typography>
          <Box px={1.5}>
            {measures.map((measure, i) => (
              <Box
                key={measure.id}
                className={`${i < measures.length - 1 ? "border-bottom" : ""}`}
                sx={{ py: 1.3 }}
              >
                <Typography
                  mr={0.5}
                  color="secondary"
                  fontWeight={600}
                  variant="body1"
                  component="span"
                >
                  {`${t("Resolution.measure")} #${i + 1}: `}
                </Typography>
                <Typography
                  color="primary"
                  variant="body1"
                  component="span"
                  fontWeight={700}
                >
                  {measure.measure !== "another"
                    ? getLabelByValue(measureOptions, measure.measure)
                    : measure.another_measure}
                </Typography>
                {measure.comment && (
                  <Comments
                    variant="body1"
                    text={measure.comment}
                    background="white"
                    padding={0.33}
                    maxLength={250}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Sanciones */}
      {sanctions.length > 0 && (
        <Box my={2}>
          <Typography
            variant="subtitle1"
            color="secondary"
            fontWeight={600}
            gutterBottom
            sx={{
              backgroundColor: theme.palette.orange.main,
              color: theme.palette.background.paper,
              px: 2,
              py: 1,
              borderRadius: 1
            }}
          >
            {t("Resolution.sanctions")}
          </Typography>
          <Box px={1.5}>
            {sanctions.map((sanction, i) => (
              <Box
                key={sanction.id}
                className={`${i < sanctions.length - 1 ? "border-bottom" : ""}`}
                sx={{ py: 1.3 }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <EGIconButton
                    iconName={sanction.confirm ? "outline_profile_tick" : "outline_profile_remove"}
                    color={sanction.confirm ? "orange" : "blueGray500"}
                    title={sanction.confirm ? t("Resolution.confirmed") : t("Resolution.unconfirmed")}
                  />
                  <Box>
                    <Typography
                      mr={0.5}
                      color="secondary"
                      fontWeight={600}
                      variant="body1"
                      component="span"
                    >
                      {`${involvedType[sanction.kind]}: `}
                    </Typography>
                    <Typography
                      color="orange"
                      variant="body1"
                      component="span"
                      fontWeight={700}
                    >
                      {sanction.name}
                    </Typography>
                  </Box>
                </Stack>

                <Grid2 container>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <List dense>
                      <ListSubheader component={"h6"} sx={{ px: 0.5, py: 0, lineHeight: "15px" }}>
                        {`${t("Resolution.assigned_sanctions")}:`}
                      </ListSubheader>
                      {sanction.sanctions &&
                        sanction.sanctions.map((sanctionValue, idx) => {
                          const savedLabel = sanction.sanctions_label?.[sanctionValue];
                          const displayLabel = savedLabel ||
                            sanctionsOptions.find((opt) => opt.value === sanctionValue)?.label ||
                            sanctionValue;

                          return (
                            <ListItem sx={{ px: 1, py: 0 }} key={`sanction-${idx}`}>
                              <ListItemIcon sx={{ minWidth: "25px" }}>
                                <BoldJudge color={theme.palette.orange.main} size={20} />
                              </ListItemIcon>
                              <ListItemText primary={displayLabel} primaryTypographyProps={{ variant: "body1" }} />
                            </ListItem>
                          );
                        })}
                      {sanction.another &&
                        sanction.another.map((another) => (
                          <ListItem sx={{ px: 1, py: 0 }} key={`another-${another.id}`}>
                            <ListItemIcon sx={{ minWidth: "25px" }}>
                              <BoldJudge color={theme.palette.orange.main} size={20} />
                            </ListItemIcon>
                            <ListItemText
                              primary={another.another_sanction}
                              primaryTypographyProps={{ variant: "body1" }}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Grid2>

                  {sanction.other_recommendations && sanction.other_recommendations.length > 0 && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <List dense>
                        <ListSubheader component={"h6"} sx={{ px: 0.5, py: 0, lineHeight: "15px" }}>
                          {`${t("Resolution.other_recommendations")}:`}
                        </ListSubheader>
                        {sanction.other_recommendations.map((recValue, idx) => {
                          const savedLabel = sanction.other_recommendations_label?.[recValue];
                          const displayLabel = savedLabel || recValue;

                          return (
                            <ListItem sx={{ px: 1, py: 0 }} key={`other-rec-${idx}`}>
                              <ListItemIcon sx={{ minWidth: "25px" }}>
                                <BoldJudge color={theme.palette.primary.main} size={20} />
                              </ListItemIcon>
                              <ListItemText
                                primary={displayLabel}
                                primaryTypographyProps={{ variant: "body1" }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Grid2>
                  )}

                  {sanction.comment && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Typography variant="h6" color="secondary">
                        {`${t("Resolution.details")}:`}
                      </Typography>
                      <Comments
                        text={sanction.comment}
                        background="white"
                        padding={1}
                        maxLength={250}
                        border="blueGray50"
                        rounded
                      />
                    </Grid2>
                  )}
                </Grid2>

                <Stack direction="column" sx={{ px: 1, py: 0.5 }}>
                  {sanction.determination_date && (
                    <Typography variant="body1" component="span">
                      {`${t("Resolution.determination_date")}: ${sanction.determination_date}`}
                    </Typography>
                  )}
                  {sanction.execution_date && (
                    <Typography variant="body1" component="span">
                      {`${t("Resolution.execution_date")}: ${sanction.execution_date}`}
                    </Typography>
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Compensaciones */}
      {compensations.length > 0 && (
        <Box my={2}>
          <Typography
            variant="subtitle1"
            color="secondary"
            fontWeight={600}
            gutterBottom
            sx={{
              backgroundColor: theme.palette.vividViolet.main,
              color: theme.palette.background.paper,
              px: 2,
              py: 1,
              borderRadius: 1
            }}
          >
            {t("Resolution.remunerations_compesation")}
          </Typography>
          <Box px={1.5}>
            {compensations.map((compensation, i) => (
              <Box
                key={compensation.id}
                className={`${i < compensations.length - 1 ? "border-bottom" : ""}`}
                sx={{ py: 1.3 }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <EGIconButton
                    iconName={compensation.confirm ? "outline_profile_tick" : "outline_profile_remove"}
                    color={compensation.confirm ? "vividViolet" : "blueGray500"}
                    title={compensation.confirm ? t("Resolution.confirmed") : t("Resolution.unconfirmed")}
                  />
                  <Box>
                    <Typography
                      mr={0.5}
                      color="secondary"
                      fontWeight={600}
                      variant="body1"
                      component="span"
                    >
                      {`${involvedType[compensation.kind]}: `}
                    </Typography>
                    <Typography
                      color="vividViolet"
                      variant="body1"
                      component="span"
                      fontWeight={700}
                    >
                      {compensation.name}
                    </Typography>
                  </Box>
                </Stack>

                <Grid2 container>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <List dense>
                      <ListSubheader component={"h6"} sx={{ px: 0.5, py: 0, lineHeight: "15px" }}>
                        {`${t("Resolution.benefits_granted")}:`}
                      </ListSubheader>
                      {compensation.benefits &&
                        compensation.benefits.map((benefitValue, idx) => {
                          const savedLabel = compensation.benefits_label?.[benefitValue];
                          const displayLabel = savedLabel ||
                            benefitOptions.find((opt) => opt.value === benefitValue)?.label ||
                            benefitValue;

                          return (
                            <ListItem sx={{ px: 1, py: 0 }} key={`benefit-${idx}`}>
                              <ListItemIcon sx={{ minWidth: "25px" }}>
                                <BoldJudge color={theme.palette.vividViolet.main} size={20} />
                              </ListItemIcon>
                              <ListItemText primary={displayLabel} primaryTypographyProps={{ variant: "body1" }} />
                            </ListItem>
                          );
                        })}
                      {compensation.another &&
                        compensation.another.map((another) => (
                          <ListItem sx={{ px: 1, py: 0 }} key={`another-${another.id}`}>
                            <ListItemIcon sx={{ minWidth: "25px" }}>
                              <BoldJudge color={theme.palette.vividViolet.main} size={20} />
                            </ListItemIcon>
                            <ListItemText
                              primary={another.another_benefit}
                              primaryTypographyProps={{ variant: "body1" }}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Grid2>

                  {compensation.other_recommendations && compensation.other_recommendations.length > 0 && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <List dense>
                        <ListSubheader component={"h6"} sx={{ px: 0.5, py: 0, lineHeight: "15px" }}>
                          {`${t("Resolution.other_recommendations")}:`}
                        </ListSubheader>
                        {compensation.other_recommendations.map((recValue, idx) => {
                          const savedLabel = compensation.other_recommendations_label?.[recValue];
                          const displayLabel = savedLabel || recValue;

                          return (
                            <ListItem sx={{ px: 1, py: 0 }} key={`other-rec-${idx}`}>
                              <ListItemIcon sx={{ minWidth: "25px" }}>
                                <BoldJudge color={theme.palette.primary.main} size={20} />
                              </ListItemIcon>
                              <ListItemText
                                primary={displayLabel}
                                primaryTypographyProps={{ variant: "body1" }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Grid2>
                  )}

                  {compensation.comment && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Typography variant="h6" color="secondary">
                        {`${t("Resolution.details")}:`}
                      </Typography>
                      <Comments
                        text={compensation.comment}
                        background="white"
                        padding={1}
                        maxLength={250}
                        border="blueGray50"
                        rounded
                      />
                    </Grid2>
                  )}
                </Grid2>

                <Stack direction="column" sx={{ px: 1, py: 0.5 }}>
                  {compensation.determination_date && (
                    <Typography variant="body1" component="span">
                      {`${t("Resolution.determination_date")}: ${compensation.determination_date}`}
                    </Typography>
                  )}
                  {compensation.execution_date && (
                    <Typography variant="body1" component="span">
                      {`${t("Resolution.execution_date")}: ${compensation.execution_date}`}
                    </Typography>
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};
